import { Edit } from '@mui/icons-material';
import {
  Checkbox,
  Divider,
  IconButton,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import { TopicsScores } from '@/domain/impact-calculate/entities/calculate.entity';
import { ModelVersionESS } from '@/domain/project/entities/project.entity';
import { VenueDate } from '@/domain/project-venue-dates/entities/project-venue-dates.entity';
import { ScoreTag } from '@/ui/components/score/ScoreTag';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { ColorsScore } from '@/ui/theme/colors-score';

import { formatDate, getScoreForDate } from '../utils';
import { CustomAvgCapacityTag } from './CustomAvgCapacityTag';
import { MoreMenu } from './MoreMenu';
import { PresetTag } from './PresetTag';

export function VenueDateItem({
  venueDate,
  scoresByDates,
  onClickEditDay,
  onClickDelete,
  onClickPresets,
  onSelectPreset,
  isSelecting,
  isSelected,
  onToggleSelect,
}: {
  venueDate: VenueDate;
  scoresByDates: Record<number, TopicsScores>;
  onClickEditDay: (
    id: number,
    modelESS: ModelVersionESS,
    scrollTo?: string,
  ) => void;
  onClickDelete: (id: number) => void;
  onClickPresets: () => void;
  onSelectPreset: (venueDateId: number, presetId: number) => void;
  isSelecting: boolean;
  isSelected: boolean;
  onToggleSelect: (id: number) => void;
}) {
  const scores = getScoreForDate(scoresByDates, venueDate.id);

  return (
    <>
      <ListItem
        onClick={isSelecting ? () => onToggleSelect(venueDate.id) : undefined}
        sx={isSelecting ? { cursor: 'pointer' } : undefined}
      >
        {isSelecting && (
          <Checkbox
            checked={isSelected}
            onChange={() => onToggleSelect(venueDate.id)}
            onClick={(e) => e.stopPropagation()}
            sx={{ mr: 1 }}
          />
        )}
        <ListItemText>
          <Stack sx={{ flexDirection: 'row', gap: 2 }}>
            <Typography
              sx={{
                width: '10rem',
                fontSize: '1.5rem',
                fontWeight: 800,
                color: ColorsRaw.anthracite.Anthracite_5,
              }}
            >
              {formatDate(venueDate.date)}
            </Typography>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 800 }}>
              {venueDate.name}
            </Typography>
            <PresetTag
              presetId={venueDate.properties?.presetId}
              onClickPresets={onClickPresets}
              onSelectPreset={(presetId) =>
                onSelectPreset(venueDate.id, presetId)
              }
              disabled={isSelecting}
            />
            {venueDate.properties?.customAvgCapacity && (
              <CustomAvgCapacityTag
                avgCapacity={venueDate.properties.avgCapacity}
                onClick={() =>
                  onClickEditDay(
                    venueDate.id,
                    venueDate.models.ess as ModelVersionESS,
                    'day-quiz-preset-section',
                  )
                }
                disabled={isSelecting}
              />
            )}
          </Stack>
        </ListItemText>
        <Stack sx={{ flexDirection: 'row', gap: 2 }}>
          <ScoreTag
            value={venueDate.stats?.totalAll || 0}
            colors={ColorsScore.none}
            topic="persons"
            onClick={() =>
              onClickEditDay(
                venueDate.id,
                venueDate.models.ess as ModelVersionESS,
                'day-quiz-ess-section',
              )
            }
            disabled={isSelecting}
          />
          <ScoreTag
            value={scores.co2}
            colors={ColorsScore.none}
            topic="co2"
            onClick={() =>
              onClickEditDay(
                venueDate.id,
                venueDate.models.ess as ModelVersionESS,
                'day-quiz-co2-section',
              )
            }
            disabled={isSelecting}
          />
          <IconButton
            onClick={() =>
              onClickEditDay(
                venueDate.id,
                venueDate.models.ess as ModelVersionESS,
              )
            }
            disabled={isSelecting}
          >
            <Edit />
          </IconButton>
          <MoreMenu
            onClickDelete={() => onClickDelete(venueDate.id)}
            disabled={isSelecting}
          />
        </Stack>
      </ListItem>
      <Divider />
    </>
  );
}
